<script setup lang="ts">
import type { IBulkApproveModeratedRequest } from "@/api"
import type { Media } from "@/models"
import { computed } from "vue"
import { useApproveModeratedMedia, useRejectModeratedMedia } from "@/api"
import { MediaTile } from "@/components"
import { BaseModal, Button, Icon } from "@/components/ui"
import { useModal, useTranslation } from "@/composables"
import { MediaType } from "@/models"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { MediaModerateModalKey } from "@/symbols"

interface MediaModerateModalProps {
  media?: Media[]
  type?: "approve" | "reject"
}

const props = withDefaults(defineProps<MediaModerateModalProps>(), { type: "reject" })

const emit = defineEmits<{
  (e: "success", count: number, type: "approve" | "reject"): void
  (e: "close"): void
  (e: "error", error: unknown): void
}>()

const eventStore = useEventVaultStore()

const { t } = useTranslation()
const { toggle } = useModal(MediaModerateModalKey)

const { mutate: approveModeratedMedia, isPending: isApprovePending } = useApproveModeratedMedia()
const { mutate: rejectModeratedMedia, isPending: isRejectPending } = useRejectModeratedMedia()

const isPending = computed(() => isApprovePending.value || isRejectPending.value)

function onConfirm() {
  if (!eventStore.eventVaultId || !props.media?.length) {
    return
  }

  const contents = props.media.map(m => ({
    contentType: m.type === MediaType.Vid ? "Video" as const : "Photo" as const,
    contentId: m.id,
  }))

  const requestBody: IBulkApproveModeratedRequest = {
    contents,
  }

  if (props.type === "approve") {
    approveModeratedMedia({ eventVaultId: eventStore.eventVaultId, requestBody }, {
      onSuccess: () => {
        emit("success", props.media!.length, props.type)
        toggle()
      },
      onError: error => emit("error", error),
    })
  }

  else if (props.type === "reject") {
    rejectModeratedMedia({ eventVaultId: eventStore.eventVaultId, requestBody }, {
      onSuccess: () => {
        emit("success", props.media!.length, props.type)
        toggle()
      },
      onError: error => emit("error", error),
    })
  }
}

function onClose() {
  emit("close")
  toggle()
}
</script>

<template>
  <BaseModal :name="MediaModerateModalKey" data-test="media-moderate-modal">
    <div class="bg-white rounded-lg overflow-hidden max-h-[80vh] flex flex-col">
      <div class="shrink-0 bg-white p-6 pb-4 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <Icon :icon="type === 'approve' ? 'CircleCheck' : 'CircleRemove'" size="lg" />
            <h1 class="font-comfortaa font-semibold text-base md:text-xl">
              {{ type === "approve" ? t("media_moderate_modal.approve_title", { count: media?.length }) : t("media_moderate_modal.reject_title", { count: media?.length }) }}
            </h1>
          </div>
          <Button :aria-label="t('media_moderate_modal.close_modal')" variant="flat" prefix-icon="Close" class="p-0" @click="onClose" />
        </div>
      </div>

      <div class="flex-1 overflow-y-auto font-montserrat text-sm md:text-base">
        <div class="p-6 flex flex-col gap-4">
          <p>
            {{ type === "approve" ? t("media_moderate_modal.approve_description") : t("media_moderate_modal.reject_description") }}
          </p>

          <div class="grid gap-3 grid-cols-1 md:grid-cols-2">
            <MediaTile
              v-for="(item, index) in media?.slice(0, 3)" :key="`media-${index}`"
              :media="item"
            />
            <div
              v-if="media?.slice(3).length"
              class="pl-4 border-gray-300 gap-4 flex items-center font-montserrat font-medium"
            >
              <div>
                <h5 class="text-gray-800 mb-1 line-clamp-2 text-ellipsis">
                  {{ t("media_delete_modal.plus_others", { count: media.slice(3).length }) }}
                </h5>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-end shrink-0 bg-white border-t border-gray-200 p-6 pt-4 w-full gap-3 flex-wrap">
        <Button variant="flat" data-test="cancel-button" class="flex-1 md:flex-none" @click="onClose">
          {{ t("cancel") }}
        </Button>
        <Button
          :variant="type === 'approve' ? 'primary' : 'danger'"
          data-test="confirm-button"
          class="flex-1 md:flex-none"
          :disabled="isPending"
          @click="onConfirm"
        >
          <Icon v-if="isPending" icon="Loading" />
          {{ type === "approve" ? t("media_moderate_modal.confirm_approve") : t("media_moderate_modal.confirm_reject") }}
        </Button>
      </div>
    </div>
  </BaseModal>
</template>
